<template>
  <div>
    <v-breadcrumbs :items="items"></v-breadcrumbs>

    <v-row>
      <v-col cols="12" md="8">
        <v-card :loading="loading" class="mb-4">
          <template slot="progress">
            <v-progress-linear height="4" indeterminate color="primary"></v-progress-linear>
          </template>

          <v-card-title class="d-flex align-center flex-wrap">
            <div>
              <div class="text-h6">{{ patient_info.patient_name || 'Patient Details' }}</div>
              <div class="subtitle-2 grey--text text--darken-1">
                ID: {{ patient_info.patient_id || '—' }}
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-chip
                v-if="patient_info.patient_blood_group"
                small
                color="red"
                dark
                class="mr-2"
            >
              {{ patient_info.patient_blood_group }}
            </v-chip>
            <v-chip v-if="patient_info.patient_sex" small outlined>
              {{ patient_info.patient_sex }}
            </v-chip>
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text>
            <v-row dense>
              <v-col cols="12" sm="6">
                <div class="info-row">
                  <div class="info-label">Date of Birth</div>
                  <div class="info-value d-flex align-center flex-wrap">
                    <span>{{ format_date(patient_info.patient_dob) || '—' }}</span>
                    <v-chip
                        v-if="patient_info.dob_not_known_check == 1"
                        x-small
                        outlined
                        color="warning"
                        class="ml-2"
                    >
                      <v-icon left x-small>mdi-alert-circle-outline</v-icon>
                      DOB Not Verified
                    </v-chip>
                    <v-chip
                        v-else-if="patient_info.patient_dob"
                        x-small
                        outlined
                        color="success"
                        class="ml-2"
                    >
                      <v-icon left x-small>mdi-check-circle-outline</v-icon>
                      DOB Verified
                    </v-chip>
                  </div>
                </div>
                <div class="info-row">
                  <div class="info-label">Contact Number</div>
                  <div class="info-value d-flex align-center">
                    <span>{{ patient_info.patient_phone || '—' }}</span>
                    <v-btn
                        v-if="checkPermissionHasOrNot(EDIT_PHONE_NUMBER)"
                        icon
                        x-small
                        class="ml-1"
                        @click="editPhoneNumber(patient_info)"
                    >
                      <v-icon x-small color="primary">mdi-pencil</v-icon>
                    </v-btn>
                  </div>
                </div>
                <div class="info-row">
                  <div class="info-label">Email</div>
                  <div class="info-value">{{ patient_info.patient_email || '—' }}</div>
                </div>
                <div class="info-row">
                  <div class="info-label">Occupation</div>
                  <div class="info-value">{{ patient_info.patient_occupation || '—' }}</div>
                </div>
                <div class="info-row">
                  <div class="info-label">Father's Name</div>
                  <div class="info-value">{{ patient_info.father_name || '—' }}</div>
                </div>
                <div class="info-row">
                  <div class="info-label">Mother's Name</div>
                  <div class="info-value">{{ patient_info.mother_name || '—' }}</div>
                </div>
              </v-col>

              <v-col cols="12" sm="6">
                <div class="info-row">
                  <div class="info-label">Address</div>
                  <div class="info-value">{{ patient_info.patient_address || '—' }}</div>
                </div>
                <div class="info-row">
                  <div class="info-label">Registration Date</div>
                  <div class="info-value">{{ patient_info.admission_date || '—' }}</div>
                </div>
                <div class="info-row">
                  <div class="info-label">Referral</div>
                  <div class="info-value">{{ referralName }}</div>
                </div>
                <div class="info-row">
                  <div class="info-label">Referral Doctor</div>
                  <div class="info-value">{{ referralDoctorName }}</div>
                </div>
                <div class="info-row">
                  <div class="info-label">National ID</div>
                  <div class="info-value">{{ patient_info.national_id || '—' }}</div>
                </div>
                <div class="info-row">
                  <div class="info-label">Birth Certificate</div>
                  <div class="info-value">{{ patient_info.birth_cert_no || '—' }}</div>
                </div>
                <div class="info-row">
                  <div class="info-label">Created By</div>
                  <div class="info-value">
                    <span v-if="patient_info.user">
                      {{ patient_info.user.full_name }}
                      <span v-if="patient_info.creation_type">({{ patient_info.creation_type }})</span>
                    </span>
                    <span v-else>—</span>
                  </div>
                </div>
                <div class="info-row">
                  <div class="info-label">Created Time</div>
                  <div class="info-value">{{ format_date(patient_info.created_at) || '—' }}</div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card class="mb-4" v-if="patient_info.patient_id">
          <v-card-title class="subtitle-1">Quick Actions</v-card-title>
          <v-card-text>
            <v-btn
                block
                color="warning"
                class="mb-2"
                depressed
                @click="editItem(patient_info)"
            >
              <v-icon left small>mdi-pencil</v-icon>
              Edit Patient
            </v-btn>
            <v-btn
                v-if="checkPermissionHasOrNot(EDIT_PHONE_NUMBER)"
                block
                color="primary"
                class="mb-2"
                outlined
                @click="editPhoneNumber(patient_info)"
            >
              <v-icon left small>mdi-cellphone</v-icon>
              Edit Phone Number
            </v-btn>
            <v-btn
                block
                color="success"
                class="mb-2"
                depressed
                @click="get_appointment(patient_info)"
            >
              <v-icon left small>mdi-calendar</v-icon>
              Set Appointment
            </v-btn>
            <v-btn
                block
                color="info"
                class="mb-2"
                depressed
                @click="get_ot_schedule(patient_info)"
            >
              <v-icon left small>mdi-calendar-clock</v-icon>
              Set OT Schedule
            </v-btn>
            <v-btn
                block
                color="warning"
                outlined
                @click="create_invoice_investigation(patient_info)"
            >
              <v-icon left small>mdi-file-document-plus</v-icon>
              Create Investigation Invoice
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card>
      <v-card-title>
        Service List
        <v-chip v-if="desserts.length" small class="ml-2" color="primary" outlined>
          {{ desserts.length }}
        </v-chip>
        <v-spacer></v-spacer>
        <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search services"
            single-line
            hide-details
            dense
            outlined
            style="max-width: 260px"
        ></v-text-field>
      </v-card-title>
      <v-data-table
          :headers="headers"
          :items="desserts"
          :search="search"
          :loading="loading"
          dense
          class="elevation-0"
      >
        <template v-slot:item.invoice_description="{ item }">
          <span v-html="item.invoice_description"></span>
        </template>
        <template v-slot:no-data>
          <div class="py-6 grey--text">No service records found</div>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="addEditPhoneNumberModal" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h6">Edit Mobile Number</v-card-title>
        <v-card-text>
          <v-text-field
              v-model="submitPhoneNumberEditData.patient_phone"
              label="New Mobile Number"
              outlined
              dense
              class="mb-3"
              type="number"
              hide-details="auto"
          ></v-text-field>
          <v-text-field
              v-model="submitPhoneNumberEditData.request_user_comment"
              label="Reason for change"
              outlined
              dense
              hide-details="auto"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="addEditPhoneNumberModal = false">Cancel</v-btn>
          <v-btn color="primary" depressed @click="updatePhoneNumber()">Submit</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.info-row {
  margin-bottom: 12px;
}
.info-label {
  font-size: 12px;
  color: #757575;
  margin-bottom: 2px;
}
.info-value {
  font-size: 14px;
  color: #212121;
  font-weight: 500;
}
</style>

<script>
import atshq from "../../../js/setup";
import moment from "moment";
import PermissionName from "@/js/PermissionName";

export default {
  data() {
    return {
      EDIT_PHONE_NUMBER: PermissionName.EDIT_PHONE_NUMBER,
      patient_id: this.$route.params.id,
      search: "",
      loading: true,
      addEditPhoneNumberModal: false,
      login_user_id: localStorage.getItem("userId"),
      submitPhoneNumberEditData: {
        patient_id: '',
        patient_name: '',
        patient_phone: '',
        request_user_comment: '',
      },
      desserts: [],
      headers: [
        { text: "Invoice ID", value: "invoice_id" },
        { text: "Invoice Type", value: "invoice_type" },
        { text: "Service Name", value: "invoice_description" },
        { text: "Eye", value: "invoice_eye_type" },
        { text: "Amount", value: "invoice_amount" },
        { text: "Date", value: "inv_date" },
      ],
      patient_info: {},
      referral_info: {
        corporate_client: { ps_name: "—" },
      },
      items: [
        { text: "Dashboard", disabled: false, href: "/" },
        { text: "Patients", disabled: false, href: "/patients" },
        { text: "Patient Details", disabled: true, href: "patient-details" },
      ],
    };
  },
  computed: {
    referralName() {
      return (this.referral_info.corporate_client && this.referral_info.corporate_client.ps_name) || '—';
    },
    referralDoctorName() {
      if (this.patient_info.doctor && this.referral_info.doctor) {
        return this.referral_info.doctor.dr_name || '—';
      }
      return '—';
    },
  },
  created() {
    this.initialize();
  },
  methods: {
    initialize() {
      this.loading = true;
      atshq
          .get("patients/patient-profile/" + this.patient_id)
          .then((response) => {
            this.loading = false;
            this.patient_info = response.data.data.patient || {};
            this.referral_info = response.data.data.patient || { corporate_client: { ps_name: '—' } };
            this.desserts = response.data.data.invoice || [];
          })
          .catch((e) => {
            this.loading = false;
            this.$noty.error("Failed to load patient details");
            console.log(e);
          });
    },
    format_date(value) {
      if (value) {
        return moment(String(value)).format('DD MMMM YYYY');
      }
      return '';
    },
    editItem(item) {
      window.location.href = "/patient-create/" + item.patient_id;
    },
    get_appointment(item) {
      window.location.href = "/appointment/create/" + item.patient_id;
    },
    get_ot_schedule(item) {
      window.location.href = "/ot-module/schedule-create?patient_id=" + item.patient_id;
    },
    create_invoice_investigation(item) {
      window.open("/invoice/invoice-create-investigation/" + item.patient_id);
    },
    editPhoneNumber(patient_info) {
      this.submitPhoneNumberEditData.patient_id = patient_info.patient_id;
      this.submitPhoneNumberEditData.patient_name = patient_info.patient_name;
      this.submitPhoneNumberEditData.patient_phone = '';
      this.submitPhoneNumberEditData.request_user_comment = '';
      this.addEditPhoneNumberModal = true;
    },
    updatePhoneNumber() {
      if (!this.submitPhoneNumberEditData.patient_phone || !this.submitPhoneNumberEditData.request_user_comment) {
        this.$noty.error('Please enter phone number and reason');
        return;
      }
      if (confirm("Are you sure you want to update the phone number?")) {
        atshq.post("patients/change-phone-number", {
          submitData: this.submitPhoneNumberEditData,
          login_user_id: this.login_user_id,
        }).then((response) => {
          this.$noty.success(response.data.message);
          this.addEditPhoneNumberModal = false;
          this.initialize();
        }).catch((e) => {
          const msg = (e.response && e.response.data && e.response.data.message) || 'Something went wrong';
          this.$noty.error(msg);
        });
      }
    },
  },
};
</script>
